import { Provider } from "@redotech/redo-order-common/provider";
import type { Channel } from "@redotech/redo-order-common/sales-channels";
import { SalesChannels } from "@redotech/redo-order-common/sales-channels";
import {
  DiscountDistributionMethod,
  OutOfStockAdvanced,
} from "@redotech/redo-return-core-common/exchange-settings";
import { ReturnedProductStatus } from "@redotech/redo-return-core-common/returned-product-status";
import { compareMaybeGids } from "@redotech/redo-shopify-common/gid-utils";
import { assertNever, isDefined } from "@redotech/util/type";
import { ObjectId } from "bson";
import * as z from "zod";
import {
  isRedoLineItem,
  type LineItem,
  type Order,
  type OriginItem,
} from "./order";
import {
  getOriginProductTotals,
  MaybeOriginOrderArray,
  OriginOrder,
} from "./origin-order-utils";
import {
  AdjustmentType,
  AdjustmentWithType,
  EssentialRefundSchema,
  MerchantAppReturn,
  UpsellBillingTrigger,
  type Settlement,
} from "./return";
import { chargesExchangeShippingViaStripe as computeChargesExchangeShippingViaStripe } from "./returns/return-totals/exchange-shipping-billing";
import { temporaryOrderTaxIncludedList } from "./temporary-fix-taxes-included-discrepancies";
import { THIRD_PARTY_SOURCE } from "./third-party";

export const INSTANT_REFUND_FEE_PERCENTAGE = 0.05;

export function getReturnTotalsFrontendLogFn() {
  return (level: LogLevel, message: string, data?: object) => {
    switch (level) {
      case LogLevel.INFO:
        console.log(message, data);
        break;
      case LogLevel.WARN:
        console.warn(message, data);
        break;
      case LogLevel.ERROR:
        console.error(message, data);
        break;
      default:
        assertNever(level);
    }
  };
}

export interface ReturnLoyaltyData {
  enabled: boolean;
  pointsPerDollar: number;
  currentPoints: number;
  loyaltyPointsRedeemedOnOrder: number;
  lineItemPointsEarned: { [lineItemId: string]: number };
}

/** @deprecated Use ReturnTotalsSet instead */
export interface ReturnTotals {
  productTotals: ProductTotals[];
  // Store credit not including original shipping refund.
  totalStoreCredit: number;
  // Store credit including original shipping refund.
  totalStoreCreditWithShipping: number;
  // Refund not including original shipping refund.
  totalRefundWithoutShipping: number;
  // Refund including original shipping refund. The shipping refund is NOT applied towards exchanges.
  totalRefundWithShipping: number;
  /**
   * Includes taxes and adjustments
   *
   * totalReturnValue = totalRawReturnValue + totalAdjustmentValue + totalTaxes
   */
  totalReturnValue: number;

  /**
   * Does not include taxes and adjustments
   *
   * totalRawReturnValue = sum(discount(price) of each item being returned)
   */
  totalRawReturnValue: number;
  /**
   * Sum total of all upsells and fees (not including merchant adjustments) on every item being returned.
   * Adjustment value is calculated on the discounted price of each item being refunded, NOT including any tax.
   *
   * totalAdjustmentValue = adjustment(totalRawReturnValue)
   */
  totalAdjustmentValue: number;
  /** Sum total of all merchant adjustments on every item being returned. */
  totalMerchantAdjustment: number;
  /** Does not include Redo discounts */
  totalDiscount: number;
  /**
   * Includes discounts and taxes
   *
   * newOrderValue = rawNewOrderValue + totalNewOrderTaxes (not advancedExchangeItemsTaxInNewOrder)
   */
  newOrderValue: number;
  /**
   * Includes discounts, but does not include taxes or adjustments
   *
   * rawNewOrderValue = sum(discount(price) of each item on the new order)
   */
  rawNewOrderValue: number;
  newOrderDiscount: number;
  /**
   * Taxes on all items in the new order, including taxes on exchanged items (which we don't charge).
   * Don't use this for charging taxes on advanced exchanges - use advancedExchangeItemsTaxInNewOrder instead.
   *
   * totalNewOrderTaxes = taxes(rawNewOrderValue))
   */
  totalNewOrderTaxes: number;
  /**
   * Taxes on advanced exchange items in the new order (not the whole new order).
   * Is included in totalNewOrderTaxes.
   *
   * We only charge taxes for advanced exchange items, so to the back-end, these are the only taxes
   * the new order has. But to the front-end, these are only a subset of the taxes.
   * Use totalNewOrderTaxes if you need all taxes, not just for advanced exchange items.
   *
   * advancedExchangeItemsTaxInNewOrder = taxes(discount(price) of each advanced exchange item)
   */
  advancedExchangeItemsTaxInNewOrder: number;
  advancedExchangeItemsValue: number;
  /**
   * Taxes on all items being returned, not including adjustments
   *
   * totalTaxes = taxes(totalRawReturnValue)
   */
  totalTaxes: number;
  /**
   * Taxes on all items being returned, not including adjustments, by bypassing shopify.taxes_included
   * This is useful when shopify.taxes_included is true but we still want to get the tax amounts instead of early returning 0
   */
  totalRawTaxes: number;
  totalDuty: number;
  /**
   * Difference between the values of the return and the new order (pre-tax)
   *
   * charge = totalRawReturnValue - rawNewOrderValue
   */
  charge: number;
  /**
   * Difference between the values of the return and the new order
   *
   * displayCharge = totalReturnValue - newOrderValue
   */
  displayCharge: number;
  recoveryFee: number;
  shippingFee: number;
  shippingFeeDeducted: number;
  shippingFeeDeductedFromStoreCredit: number;
  shippingFeeDeductedFromRefund: number;
  shippingFeeDeductedFromExchange: number;

  /**
   * The merchant can require a customer label upcharge fee for returns, this is the amount the customer is charged (either through deducting or stripe)
   */
  customerLabelUpchargeFee: number;
  /**
   * If the merchant has preDiscountPrice enabled, this is the difference between the original price and the discount price.
   * Can be used for "shop now" but doesn't roll over as excess store credit.
   */
  nonAccruableCredit: number;
  /** Accruable credit that was not used to cover the exchange */
  nonAccruableLeftover: number;
  /** Value of final sale items on the return which should be credited to the merchant once we issue refunds, store-credit, or an exchange */
  finalSaleCredit: number;
  /** Total shipping cost of the original order */
  totalOrderShipping: number;
  /** displayCharge + shippingFee (when the label isn't deducted from store credit/refund) */
  totalPaymentOwed: number;
  /** If the shopper had paid for the exchange order upsell, the positive merchant adjustments should be added to the store credit/refund amounts instead of deducting them from the charge. */
  merchantAdjustmentsShouldBeAddedToSCRefund: boolean;
  /** If the customer is returning an item they purchased using loyalty points, we will refund them those points */
  totalLoyaltyPointsRefunded: number;
  /** If the customer is returning an item that they received points for, we will claw back those points */
  totalLoyaltyPointsDeducted: number;
  totalReturnValueDeductedDueToLoyaltyPoints: number;
  /** Exchange shipping fee for new items being sent to customer */
  exchangeShippingFee: number;
  /** Name of the exchange shipping method */
  exchangeShippingName: string;
  /**
   * Whether the customer-selected exchange shipping fee is collected via
   * Stripe at submit time. When true, the fee is included in
   * `totalPaymentOwed` as a Stripe charge. When false, it's folded into
   * `displayCharge` because the fee rides on the exchange draft order.
   *
   * NOTE: this is settings-shaped, not totals-shaped — it's a passthrough
   * computed from `team.settings.exchanges.forceExchangeStripeBilling` +
   * `return_.upsellBillingTrigger`. The new RAC's `ReturnSummaryTotals`
   * has dropped this; consumers should prefer reading the persisted
   * `return_.chargesExchangeShippingViaStripe` field for post-creation
   * reads, or call `chargesExchangeShippingViaStripe(...)` from
   * `@redotech/redo-model/returns/return-totals/exchange-shipping-billing`
   * for pre-creation. Kept on the legacy output for back-compat with
   * existing display surfaces.
   */
  chargesExchangeShippingViaStripe: boolean;
}

export const ReturnTotalsSchema = z.object({
  totalStoreCredit: z.number(),
  totalStoreCreditWithShipping: z.number(),
  totalRefundWithoutShipping: z.number(),
  totalRefundWithShipping: z.number(),
  totalReturnValue: z.number(),
  totalRawReturnValue: z.number(),
  totalAdjustmentValue: z.number(),
  totalMerchantAdjustment: z.number(),
  totalDiscount: z.number(),
  newOrderValue: z.number(),
  rawNewOrderValue: z.number(),
  newOrderDiscount: z.number(),
  totalNewOrderTaxes: z.number(),
  advancedExchangeItemsTaxInNewOrder: z.number(),
  totalTaxes: z.number(),
  totalDuty: z.number(),
  charge: z.number(),
  displayCharge: z.number(),
  recoveryFee: z.number(),
  shippingFee: z.number(),
  shippingFeeDeducted: z.number(),
  shippingFeeDeductedFromStoreCredit: z.number(),
  shippingFeeDeductedFromRefund: z.number(),
  shippingFeeDeductedFromExchange: z.number(),
  nonAccruableCredit: z.number(),
  nonAccruableLeftover: z.number(),
  finalSaleCredit: z.number(),
  totalOrderShipping: z.number(),
  totalPaymentOwed: z.number(),
  merchantAdjustmentsShouldBeAddedToSCRefund: z.boolean(),
  totalLoyaltyPointsRefunded: z.number(),
  totalLoyaltyPointsDeducted: z.number(),
  totalReturnValueDeductedDueToLoyaltyPoints: z.number(),
  exchangeShippingFee: z.number(),
  exchangeShippingName: z.string(),
  chargesExchangeShippingViaStripe: z.boolean(),
});

export const ProductTotalsSchema = z.array(
  z.object({
    product: z.object({
      line_item_id: z.string().or(z.number()).optional(),
      variant_id: z.string(),
      price: z.string(),
      price_adjustment: z.string().nullish(),
      merchant_adjustment: z.string().nullish(),
      strategy: z.string().nullish(),
      status: z.string().nullish(),
    }),
    discountPrice: z.number(),
    originalPrice: z.number(),
    finalPrice: z.number(),
    priceAdjustment: z.number(),
    merchantAdjustment: z.number(),
    tax: z.number(),
    duty: z.number(),
    totalDiscount: z.number(),
    returnValueDeductedDueToLoyaltyPoints: z.number(),
    loyaltyPointsRefunded: z.number(),
    loyaltyPointsDeducted: z.number(),
    refund_item: z.number(),
    refund_tax: z.number(),
    total_refund: z.number(),
    refund_currency: z.string().optional(),
  }),
);

export interface ProductTotals {
  product: Product;
  /** Includes discounts (if any). Does not include tax or price adjustment. */
  discountPrice: number;
  /** Does not include discounts, tax, or price adjustment */
  originalPrice: number;
  /** Includes discounts, tax, price adjustment, and merchant adjustment (if any) */
  finalPrice: number;
  priceAdjustment: number;
  merchantAdjustment: number;
  dynamicBundleAdjustment: number;
  /** This is an adjusted tax amount that may technically not include all taxes for the products.
   *  For example, when shopify.taxes_included is true then it will be 0*/
  tax: number;
  /** This is the raw tax amount for the product, including all taxes and bypassing shopify.taxes_included */
  rawTax: number;
  duty: number;
  /** Does not include Redo discounts */
  totalDiscount: number;
  /**
   * Customer buys item A, receives loyalty points, uses those points to buy item B, then returns item A. We can no longer claw back those points
   * since they were used, so we need to deduct the amount from the return value
   */
  returnValueDeductedDueToLoyaltyPoints: number;
  loyaltyPointsRefunded: number;
  loyaltyPointsDeducted: number;
  /** Used for Loop-compatible API */
  total_refund: number;
  refund_tax: number;
  refund_item: number;
  refund_currency: string;
}

export type Product = {
  _id?: ObjectId | string;
  line_item_id: string | number;
  variant_id: string;
  price: string;
  percentOfBundle?: number;
  price_adjustment?: string | null;
  merchant_adjustment?: string | null;
  strategy: string;
  status?: string | null;
  originalVariantOptions?: {
    strategy: string;
    price_adjustment?: string;
  } | null;
  exchangeGroupItem?: unknown;
  isUnbundled?: boolean | null;
  isNewItem?: boolean | null;
  isFinalSaleReturn?: boolean | null;
  outOfStockDuringProcessing?: boolean | null;
  /** Optional override */
  tax?: number | null;
  refundPointsRedeemedForItem?: boolean;
  deductEarnedPointsFromRefund?: boolean;
  adjustments?: AdjustmentWithType[];
} & ({ new_item?: object } | { exchange_for?: object });

export interface ProductTaxOptions {
  /** If true, return the raw tax amount by bypassing shopify.taxes_included */
  returnRawTax?: boolean;
}

interface DiscountAllocation {
  discountedAmount: { amount: string };
}

interface AdvancedExchangeItem {
  price?: string;
  quantity?: number;
  tax?: string;
  variantId?: string;
  outOfStockDuringProcessing?: boolean;
  itemValue?: string;
}

interface Return {
  refunds?: EssentialRefundSchema[];
  products: Product[];
  provision: string;
  stripe?: any;
  totals?: { fee: number; customerLabelUpchargeFee?: number };
  newOrderPriceAdjustment?: { type: "percentage" | "amount"; value: number };
  new_order_taxes?: string;
  discount_allocations?: DiscountAllocation[];
  advancedExchangeItems: AdvancedExchangeItem[];
  shippingFeeToDeduct?: number;
  draftOrderId?: number;
  labelDeductedFromCredit?: boolean;
  labelDeductedFromExchange?: boolean;
  merchant_waived_shipping_fee_deduction: boolean;
  originItems?: OriginItem[];
  originalReturnOptions?: {
    advancedExchangeItems?: {
      price?: string;
      quantity?: number;
      tax?: string;
      variantId?: string;
    }[];
    new_order_taxes?: string;
    discount_allocations: DiscountAllocation[];
  };
  usePreDiscountPrice?: boolean;
  settlement?: Settlement;
  refundOriginalShipping?: boolean;
  refundOriginalShippingAmount?: number;
  provider?: Provider | null;
  exchangeOrder?: any[];
  exchangeShippingName?: string;
  exchangeShippingFee?: number;
  newItemToExchangeRate?: number;
  upsellBillingTrigger?: UpsellBillingTrigger | null;
}

export function convertMerchantAppReturnToReturn({
  return_,
}: {
  return_: MerchantAppReturn;
}): Return {
  return {
    ...return_,
    products: return_.products as Product[],
    provision: return_.provision ?? "",
    totals: {
      ...return_.totals,
      customerLabelUpchargeFee:
        return_.totals.customerLabelUpchargeFee ?? undefined,
    },
    advancedExchangeItems:
      return_.advancedExchangeItems as AdvancedExchangeItem[],
    shippingFeeToDeduct: return_.shippingFeeToDeduct ?? undefined,
    draftOrderId: return_.draftOrderId ?? undefined,
    labelDeductedFromCredit: return_.labelDeductedFromCredit ?? undefined,
    labelDeductedFromExchange: return_.labelDeductedFromExchange ?? undefined,
    merchant_waived_shipping_fee_deduction:
      return_.merchant_waived_shipping_fee_deduction ?? false,
    originalReturnOptions: return_.originalReturnOptions
      ? {
          advancedExchangeItems: return_.originalReturnOptions
            .advancedExchangeItems as AdvancedExchangeItem[],
          new_order_taxes:
            return_.originalReturnOptions.new_order_taxes ?? undefined,
          discount_allocations: return_.originalReturnOptions
            .discount_allocations as DiscountAllocation[],
        }
      : undefined,
    settlement: return_.settlement ?? undefined,
    exchangeShippingName: return_.exchangeShippingName ?? undefined,
    exchangeShippingFee: return_.exchangeShippingFee ?? undefined,
    newItemToExchangeRate: return_.newItemToExchangeRate ?? undefined,
    refundOriginalShippingAmount:
      return_.refundOriginalShippingAmount ?? undefined,
    upsellBillingTrigger: return_.upsellBillingTrigger ?? undefined,
  };
}

interface Totals {
  amount: number;
  tax: number;
}

export type ReturnType = "refund" | "store_credit" | "exchange";

export enum LogLevel {
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}
type LogFn = (level: LogLevel, message: string, data?: object) => void;

interface Team {
  settings: {
    deductLabelFromCredit?: boolean;
    deductLabelFromRefund?: boolean;
    deductLabelFromExchange?: boolean;
    differentPricedVariantExchanges?: boolean;
    discountDistributionMethod?: DiscountDistributionMethod | null;
    exchanges: {
      usePreDiscountPrice?: boolean;
      outOfStockAdvanced: OutOfStockAdvanced;
      excessExchangeValue: Exclude<ReturnType, "exchange">;
      forceExchangeStripeBilling?: boolean | null;
    };
    returns?: { waiveRefundStoreCreditBelowAmount?: number | null } | null;
  };
}

/** @deprecated Use getReturnTotalsSet instead */
export class ReturnTotalsCalculator {
  private return_: Return;
  private productTotals: ProductTotals[];
  private orders?: Order[];
  private order: Order;
  private team: Team;
  private loyaltyData: ReturnLoyaltyData;
  private originOrders: MaybeOriginOrderArray;
  private logFn: LogFn;
  private isReturnPortal: boolean;
  /**
   * Merchants can edit the return after submission. If enabled, we will calculate
   * the totals for the original return.
   */
  private useOriginalReturn: boolean;
  private isMerchantCreated: boolean;
  private marketRate?: number;
  private newItemToExchangeRate?: number;
  private isInPresentmentCurrency?: boolean;

  constructor({
    return_,
    orders,
    order,
    team,
    loyaltyData,
    originOrders,
    logFn,
    useOriginalReturn = false,
    isMerchantCreated = false,
    marketRate,
    newItemToExchangeRate,
    isInPresentmentCurrency,
  }: {
    return_: Return;
    orders: Order[];
    order: Order;
    team: Team;
    loyaltyData: ReturnLoyaltyData;
    originOrders: MaybeOriginOrderArray;
    // This library is used in the frontend and backend, so we need to pass
    // a different log function for each environment.
    logFn: LogFn;
    useOriginalReturn?: boolean;
    isMerchantCreated?: boolean;
    marketRate?: number;
    newItemToExchangeRate?: number;
    isInPresentmentCurrency?: boolean;
  }) {
    this.return_ = return_;
    this.productTotals = [];
    this.orders = orders;
    this.order = order;
    this.team = team;
    this.loyaltyData = loyaltyData;
    this.originOrders = originOrders;
    this.logFn = logFn;
    this.useOriginalReturn = useOriginalReturn || false;
    this.marketRate = marketRate;
    this.newItemToExchangeRate = newItemToExchangeRate;
    this.calculateTotalsForProducts();
    // If a product has an _id, it means we are not on the return portal
    // (i.e. the return has already been created)
    this.isReturnPortal = !this.return_.products[0]?._id;
    this.isMerchantCreated = isMerchantCreated;
    this.isInPresentmentCurrency = isInPresentmentCurrency;
  }

  static getTaxForLineItem(lineItem: LineItem | undefined) {
    let taxValue = 0.0;
    const taxLines = lineItem?.tax_lines || [];
    for (const taxLine of taxLines) {
      taxValue += parseFloat(taxLine.price);
    }
    taxValue /= lineItem?.quantity || 1;

    return Math.round(taxValue * 100) / 100;
  }

  static getTaxForBundledPart(bundleLineItem: any, percentOfBundle: number) {
    return (
      ReturnTotalsCalculator.getTaxForLineItem(bundleLineItem) * percentOfBundle
    );
  }

  getProductOrder(product: Product): Order {
    const order = (this.orders || []).find((order) => {
      return order.shopify.line_items.some(
        (lineItem) =>
          lineItem.id.toString() === product.line_item_id.toString(),
      );
    });

    if (!order) {
      return this.order;
    }

    return order;
  }

  /**
   * Get taxes for a specific product in return
   */
  productTax(product: Product, options: ProductTaxOptions = {}): number {
    const order = this.getProductOrder(product);

    if (order.shopify.taxes_included && !options.returnRawTax) {
      return 0;
    }

    /** TODO @CanyonTurtle remove this after 30-day return window (2025-06-15) */
    if (temporaryOrderTaxIncludedList.includes(order?._id?.toString())) {
      return 0;
    }

    if (product.tax) {
      return product.tax;
    }
    const shopifyLineItem = ReturnTotalsCalculator.findLineItemFromShopifyField(
      product.line_item_id,
      order,
    );
    if (product.isUnbundled) {
      return product.tax || 0;
    }
    const originTax = ReturnTotalsCalculator.getOriginItemTax(
      shopifyLineItem,
      order,
      this.originOrders,
      this.logFn,
    );

    return originTax && order.isExchangeOrder
      ? parseFloat(originTax)
      : ReturnTotalsCalculator.getTaxForLineItem(shopifyLineItem);
  }

  /**
   * Get taxes for entire return
   *
   * @param return_ - The return object to calculate taxes for
   * @param options - Configuration options for tax calculation
   * @param options.returnRawTax - If true, return the raw tax amount which includes all taxes and bypasses shopify.taxes_included. Default: false
   * @returns The total tax amount for all products in the return, rounded to 2 decimal places
   */
  returnTax(return_: Return, options: ProductTaxOptions = {}): number {
    let taxValue = 0.0;
    const products = return_.products || [];
    for (const product of products) {
      taxValue += this.productTax(product, options);
    }
    return parseFloat(taxValue.toFixed(2));
  }

  /**
   * Get the taxes from an array of product totals
   * This is the default behavior and will return 0 if shopify.taxes_included is true
   *
   * @param productsToProcess - Array of ProductTotals to calculate taxes for
   * @param options - Configuration options for tax calculation
   * @param options.returnRawTax - If true, return the raw tax amount which includes all taxes and bypasses shopify.taxes_included. Default: false
   * @returns The total tax amount for all products, rounded to 2 decimal places
   */
  getProductTotalsTaxes(
    productsToProcess: ProductTotals[],
    options: ProductTaxOptions = {},
  ): number {
    let taxValue = 0.0;
    for (const productTotals of productsToProcess) {
      taxValue += this.productTax(productTotals.product, options);
    }
    return parseFloat(taxValue.toFixed(2));
  }

  private static getSimpleBundleStackID(
    lineItem: LineItem,
    order: Order,
  ): number | null {
    if (!lineItem.discount_allocations) {
      return null;
    }

    let bundleLineItemID = null;
    const re = new RegExp(/^Simple Bundles:/);
    const line_id_regex = new RegExp(/ID: \d+$/);

    for (const allocation of lineItem.discount_allocations) {
      const application =
        order.shopify.discount_applications[
          allocation.discount_application_index
        ];

      if (re.test(application?.title)) {
        bundleLineItemID = application?.title
          ?.match(line_id_regex)?.[0]
          .match(/\d+/)?.[0];
      }
    }

    return +bundleLineItemID;
  }

  private static getSimpleBundleItemDiscount(
    lineItem: LineItem,
    order: Order,
    originBundleID: number,
  ): number {
    let bundledItemsTotalPriceValue = 0;
    const itemPrice = +lineItem.price * lineItem.quantity;

    for (const item of order.shopify.line_items) {
      const bundleID = this.getSimpleBundleStackID(item, order);

      if (originBundleID === bundleID) {
        bundledItemsTotalPriceValue += item.price * item.quantity;
      }
    }

    const itemProportion = itemPrice / bundledItemsTotalPriceValue;
    const bundleItem = order.shopify.line_items.find(
      (item) => item.id === originBundleID,
    );
    if (!bundleItem) {
      return 0;
    }

    const bundleDiscounts =
      bundleItem.discount_allocations?.reduce(
        (acc: number, discount: any) => (acc += Number(discount.amount)),
        0,
      ) || 0;
    const bundlePrice =
      Number(bundleItem.price) * Number(bundleItem.quantity) - bundleDiscounts;

    const proportionalPrice = bundlePrice * itemProportion;
    const finalDiscount = itemPrice - proportionalPrice;

    return finalDiscount;
  }

  private static isLineItemCanceled(lineItem: LineItem) {
    return (
      lineItem.fulfillment_status === null &&
      lineItem.fulfillable_quantity === 0
    );
  }

  /**
   * Get the discount for a specific line item. This may not be the same as the discount
   * that was applied to the line item in the original order, depending on the
   * distributionMethod parameter.
   * @param lineItem
   * @param order
   * @param distributionMethod
   * @returns
   */
  static getDiscountAllocations(
    lineItem: LineItem | undefined,
    order: Order,
    distributionMethod: DiscountDistributionMethod = DiscountDistributionMethod.LINE_ITEM,
  ) {
    if (!lineItem) {
      return 0;
    }

    const discountAllocations = [...lineItem.discount_allocations];

    // Check to see if item is part of a 'simple bundle'
    // If so calculate its discount
    const originBundleID = this.getSimpleBundleStackID(lineItem, order);
    if (originBundleID) {
      return this.getSimpleBundleItemDiscount(lineItem, order, originBundleID);
    }

    for (const item of order.shopify.line_items) {
      if (this.isLineItemCanceled(item)) {
        continue;
      }
      for (const discount of item.discount_allocations) {
        if (
          !this.isRedoItem(item) &&
          !discountAllocations.some(
            (allocation: any) =>
              allocation.discount_application_index ===
              discount.discount_application_index,
          ) &&
          (distributionMethod === DiscountDistributionMethod.ORDER ||
            discount.discount_application_index == undefined) &&
          ["entitled", "explicit"].includes(
            order.shopify.discount_applications[
              discount.discount_application_index
            ]?.target_selection,
          )
        ) {
          discountAllocations.push(discount);
        }
      }
    }

    if (!discountAllocations?.length) {
      return 0;
    }

    let discountAmount = 0;
    for (const discount of discountAllocations) {
      // Ignore redo discounts as we want the return value to include
      // discounts from previous returns (i.e. customers should be
      // able to return items that were previously returned)

      // Discounts are distributed differently according to team settings
      let totalAmountDiscountedFromOrder = 0;
      let totalValueOfDiscountedItems = 0;

      if (distributionMethod === DiscountDistributionMethod.LINE_ITEM) {
        totalAmountDiscountedFromOrder = +discount.amount;
        totalValueOfDiscountedItems = +lineItem?.price * lineItem.quantity;
      } else {
        const acrossOrder =
          distributionMethod === DiscountDistributionMethod.ORDER;
        for (const item of order.shopify.line_items) {
          if (this.isLineItemCanceled(item)) {
            continue;
          }
          if (!this.isRedoItem(item)) {
            totalValueOfDiscountedItems += acrossOrder
              ? +item.price * item.quantity
              : 0;
            for (const allocation of item.discount_allocations) {
              if (
                allocation.discount_application_index ===
                discount.discount_application_index
              ) {
                totalAmountDiscountedFromOrder += +allocation.amount;
                totalValueOfDiscountedItems += acrossOrder
                  ? 0
                  : +item.price * item.quantity;
              }
            }
          }
        }
      }

      const discount_code =
        order.shopify.discount_applications[discount.discount_application_index]
          ?.code;
      if (
        !(
          discount_code?.substring(0, 4) === "REDO" &&
          discount_code?.length === 19
        ) &&
        totalValueOfDiscountedItems > 0
      ) {
        discountAmount +=
          totalAmountDiscountedFromOrder *
          ((+lineItem?.price * lineItem.quantity) /
            totalValueOfDiscountedItems);
      }
    }

    return discountAmount;
  }

  static isRedoItem(lineItem: LineItem | undefined) {
    return isRedoLineItem(lineItem);
  }

  static getTaxRateForLineItem(lineItem: LineItem) {
    return (lineItem?.tax_lines || []).reduce((total, taxLine) => {
      return total + (taxLine?.rate ?? 0);
    }, 0);
  }

  private static getOriginItemPrice(
    lineItem: LineItem | undefined,
    order: Order | undefined,
    originOrders: MaybeOriginOrderArray,
    logFn: LogFn,
  ) {
    if (order?.shopify.source_name === THIRD_PARTY_SOURCE) {
      return null;
    }

    try {
      const originOrder = ReturnTotalsCalculator.getOriginOrderForLineItem(
        lineItem,
        originOrders,
      );
      if (originOrder && lineItem && !("error" in originOrder)) {
        const originProductTotals = getOriginProductTotals(
          lineItem.id,
          lineItem.variant_id,
          originOrder,
        );
        if ("error" in originProductTotals) {
          logFn(
            LogLevel.WARN,
            `ORIGIN ORDERS: Error getting product totals for origin item price.
            This can happen if the merchant manually edited the exchange order.`,
            { error: originProductTotals.error, orderId: order?.shopify.id },
          );
        } else {
          return originProductTotals.price;
        }
      } else if (!lineItem) {
        logFn(LogLevel.ERROR, "ORIGIN ORDERS: No line item found", {
          orderId: order?.shopify.id,
        });
      }
    } catch (e) {
      logFn(
        LogLevel.ERROR,
        `ORIGIN ORDERS: Error getting origin item price: ${e}`,
        { orderId: order?.shopify.id, lineItemId: lineItem?.id },
      );
    }

    return null;
  }

  private static getOriginOrderForLineItem(
    lineItem: LineItem | undefined,
    originOrders: MaybeOriginOrderArray,
  ): OriginOrder | undefined {
    return originOrders.find(
      (maybeOriginOrder): maybeOriginOrder is OriginOrder =>
        maybeOriginOrder &&
        !("error" in maybeOriginOrder) &&
        maybeOriginOrder.lineItems.some(
          (li) => lineItem && compareMaybeGids(lineItem.id, li.id),
        ),
    );
  }

  private static getOriginItemDiscountPrice(
    lineItem: LineItem | undefined,
    order: Order | undefined,
    originOrders: MaybeOriginOrderArray,
    logFn: LogFn,
  ) {
    if (order?.shopify.source_name === THIRD_PARTY_SOURCE) {
      return null;
    }

    try {
      const originOrder = ReturnTotalsCalculator.getOriginOrderForLineItem(
        lineItem,
        originOrders,
      );
      if (lineItem && originOrder && !("error" in originOrder)) {
        const originProductTotals = getOriginProductTotals(
          lineItem.id,
          lineItem.variant_id,
          originOrder,
        );
        if ("error" in originProductTotals) {
          logFn(
            LogLevel.WARN,
            `ORIGIN ORDERS: Error getting product totals for origin item discount price.
            This can happen if the merchant manually edited the exchange order.`,
            { error: originProductTotals.error, orderId: order?.shopify.id },
          );
        } else {
          return originProductTotals.discountPrice;
        }
      } else if (!lineItem) {
        logFn(LogLevel.ERROR, "ORIGIN ORDERS: No line item found", {
          orderId: order?.shopify.id,
        });
      }
    } catch (e) {
      logFn(
        LogLevel.ERROR,
        `ORIGIN ORDERS: Error getting origin item discount price: ${e}`,
        { orderId: order?.shopify.id, lineItemId: lineItem?.id },
      );
    }

    return null;
  }

  static getOriginItemTax(
    lineItem: LineItem | undefined,
    order: Order | undefined,
    originOrders: MaybeOriginOrderArray,
    logFn: LogFn,
  ) {
    if (order?.shopify.source_name === THIRD_PARTY_SOURCE) {
      return null;
    }

    try {
      const originOrder = ReturnTotalsCalculator.getOriginOrderForLineItem(
        lineItem,
        originOrders,
      );
      if (lineItem && originOrder && !("error" in originOrder)) {
        const originProductTotals = getOriginProductTotals(
          lineItem.id,
          lineItem.variant_id,
          originOrder,
        );
        if ("error" in originProductTotals) {
          logFn(
            LogLevel.WARN,
            `ORIGIN ORDERS: Error getting product totals for origin item tax.
            This can happen if the merchant manually edited the exchange order.`,
            { error: originProductTotals.error, orderId: order?.shopify.id },
          );
        } else {
          return originProductTotals.tax;
        }
      } else if (!lineItem) {
        logFn(LogLevel.ERROR, "ORIGIN ORDERS: No line item found", {
          orderId: order?.shopify.id,
        });
      }
    } catch (e) {
      logFn(
        LogLevel.ERROR,
        `ORIGIN ORDERS: Error getting origin item tax: ${e}`,
        { orderId: order?.shopify.id, lineItemId: lineItem?.id },
      );
    }

    return null;
  }

  static getDiscountPriceNew({
    lineItem,
    order,
    originOrders,
    logFn,
    distributionMethod = DiscountDistributionMethod.LINE_ITEM,
    percentOfBundle = 1,
  }: {
    lineItem: LineItem;
    order: Order;
    originOrders: MaybeOriginOrderArray;
    logFn: LogFn;
    distributionMethod?: DiscountDistributionMethod;
    percentOfBundle?: number;
  }) {
    let discountPrice = 0;
    const originPrice = ReturnTotalsCalculator.getOriginItemDiscountPrice(
      lineItem,
      order,
      originOrders,
      logFn,
    );
    if (order.isExchangeOrder && originPrice) {
      discountPrice = Number(originPrice);
    } else {
      const quantity = lineItem.originalQuantity || lineItem.quantity || 1;
      const totalLineItemDiscount =
        ReturnTotalsCalculator.getDiscountAllocations(
          lineItem,
          order,
          distributionMethod,
        ) / quantity;
      discountPrice = parseFloat(lineItem.price) - totalLineItemDiscount;
    }

    return (discountPrice * percentOfBundle).toFixed(2);
  }

  static getPreDiscountPrice({
    lineItem,
    order,
    originOrders,
    logFn,
    percentOfBundle = 1,
  }: {
    lineItem: LineItem;
    order: Order;
    originOrders: MaybeOriginOrderArray;
    logFn: LogFn;
    percentOfBundle: number;
  }) {
    const originPrice = ReturnTotalsCalculator.getOriginItemPrice(
      lineItem,
      order,
      originOrders,
      logFn,
    );
    const preDiscountPrice =
      originPrice && order.isExchangeOrder
        ? parseFloat(originPrice)
        : parseFloat(lineItem.price);

    return (preDiscountPrice * percentOfBundle).toFixed(2);
  }

  /**
   * @deprecated Use getDiscountPriceNew instead
   */
  static getDiscountPriceOld(
    lineItem: LineItem | undefined,
    order: Order,
    originOrders: MaybeOriginOrderArray,
    logFn: LogFn,
    distributionMethod?: DiscountDistributionMethod,
    product?: any,
  ) {
    const originPrice = ReturnTotalsCalculator.getOriginItemDiscountPrice(
      lineItem,
      order,
      originOrders,
      logFn,
    );
    if (lineItem && product?.isUnbundled) {
      if (originPrice && order.isExchangeOrder) {
        return (Number(originPrice) * (product.percentOfBundle ?? 1)).toFixed(
          2,
        );
      }

      const totalLineItemDiscount =
        ReturnTotalsCalculator.getDiscountAllocations(
          lineItem,
          order,
          distributionMethod,
        ) / (lineItem.originalQuantity || lineItem.quantity);
      const percentOfBundle =
        parseFloat(product.price) / parseFloat(lineItem.price);

      const proportionalPrice = product.percentOfBundle
        ? parseFloat(lineItem.price) * product.percentOfBundle
        : parseFloat(product.price);

      return (
        proportionalPrice -
        totalLineItemDiscount * (product.percentOfBundle || percentOfBundle)
      ).toFixed(2);
    }
    if (originPrice && order.isExchangeOrder) {
      return originPrice;
    }

    const discountAmount = ReturnTotalsCalculator.getDiscountAllocations(
      lineItem,
      order,
      distributionMethod,
    );

    return (
      (lineItem?.price || 0) -
      discountAmount / (lineItem?.quantity || 1)
    ).toFixed(2);
  }

  private applyCreditToValue({
    credit,
    value,
  }: {
    credit: number;
    value: number;
  }) {
    const maximumCreditCanBeApplied = Math.min(value, credit);
    return {
      maximumCreditCanBeApplied,
      newValue: Math.abs(value - maximumCreditCanBeApplied),
      remainingCredit: Math.abs(credit - maximumCreditCanBeApplied),
    };
  }

  private getProductPercentOfOrder(product: Product) {
    const order = this.getProductOrder(product);
    const lineItem = ReturnTotalsCalculator.findLineItemFromShopifyField(
      product.line_item_id,
      order,
    );
    if (!lineItem) {
      return 0;
    }
    const discountPrice = ReturnTotalsCalculator.getDiscountPriceNew({
      lineItem,
      order,
      originOrders: this.originOrders,
      logFn: this.logFn,
      percentOfBundle: product.percentOfBundle,
      distributionMethod:
        this.team.settings.discountDistributionMethod ?? undefined,
    });
    // The sum of the discount prices of all line items before tax
    const totalOrderPrice = order.shopify.subtotal_price;
    return totalOrderPrice ? parseFloat(discountPrice) / totalOrderPrice : 0;
  }

  private getLoyaltyPointsForProduct(
    product: Product,
    availableLoyaltyPoints: number,
  ) {
    const {
      lineItemPointsEarned,
      loyaltyPointsRedeemedOnOrder,
      pointsPerDollar,
    } = this.loyaltyData;

    const earnedPoints = lineItemPointsEarned[product.line_item_id] || 0;
    const percentOfOrder = this.getProductPercentOfOrder(product);
    const redeemedPoints = loyaltyPointsRedeemedOnOrder * percentOfOrder;
    const pointsBalance = redeemedPoints - earnedPoints;

    let remainingPoints = availableLoyaltyPoints;
    let pointsRefunded = 0;
    let pointsDeducted = 0;
    let returnValueDeducted = 0;

    if (pointsBalance > 0) {
      pointsRefunded = pointsBalance;
    } else if (pointsBalance < 0) {
      const { remainingCredit, maximumCreditCanBeApplied, newValue } =
        this.applyCreditToValue({
          credit: remainingPoints,
          value: -pointsBalance,
        });
      remainingPoints = remainingCredit;
      pointsDeducted = maximumCreditCanBeApplied;
      returnValueDeducted = pointsPerDollar ? newValue / pointsPerDollar : 0;
    }

    return {
      returnValueDeductedDueToLoyaltyPoints: returnValueDeducted,
      loyaltyPointsRefunded: pointsRefunded,
      loyaltyPointsDeducted: pointsDeducted,
      remainingLoyaltyPoints: remainingPoints,
    };
  }

  /**
   * Calculate totals for each product, including discount price, price adjustments, and tax
   */
  private calculateTotalsForProducts() {
    let availableLoyaltyPoints = this.loyaltyData.currentPoints;
    for (const product of this.return_.products) {
      const loyalty = this.getLoyaltyPointsForProduct(
        product,
        availableLoyaltyPoints,
      );
      availableLoyaltyPoints = loyalty.remainingLoyaltyPoints;

      this.productTotals.push(
        this.calculateTotalsForProduct(product, {
          returnValueDeductedDueToLoyaltyPoints:
            loyalty.returnValueDeductedDueToLoyaltyPoints,
          loyaltyPointsRefunded: loyalty.loyaltyPointsRefunded,
          loyaltyPointsDeducted: loyalty.loyaltyPointsDeducted,
        }),
      );
    }

    const customerLabelUpchargeFee =
      this.return_.totals?.customerLabelUpchargeFee;

    if (customerLabelUpchargeFee) {
      let remainingCustomerLabelUpChargeFee = customerLabelUpchargeFee;
      const totalFinalPriceForAllProducts = this.productTotals.reduce(
        (acc, product) => acc + product.finalPrice,
        0,
      );

      for (const [index, product] of this.productTotals.entries()) {
        const isLastProduct = index === this.productTotals.length - 1;
        if (isLastProduct) {
          product.finalPrice -= remainingCustomerLabelUpChargeFee;
          product.priceAdjustment += -remainingCustomerLabelUpChargeFee;
          break;
        }
        const percentage = product.finalPrice / totalFinalPriceForAllProducts;
        let customerLabelUpChargeFeeToApply =
          customerLabelUpchargeFee * percentage;

        if (
          customerLabelUpChargeFeeToApply > remainingCustomerLabelUpChargeFee
        ) {
          customerLabelUpChargeFeeToApply = remainingCustomerLabelUpChargeFee;
        }

        remainingCustomerLabelUpChargeFee -= customerLabelUpChargeFeeToApply;
        product.finalPrice -= customerLabelUpChargeFeeToApply;
        product.priceAdjustment += -customerLabelUpChargeFeeToApply;
      }
    }
  }

  /**
   * Get the refund totals for a product
   */
  private getProductRefundTotals(product: Product) {
    let product_subtotal = 0;
    let tax_subtotal = 0;
    let refund_currency = "";
    for (const refund of this.return_.refunds || []) {
      if (!refund.refund?.refund_line_items) {
        continue;
      }
      for (const refundLineItem of refund.refund.refund_line_items) {
        if (
          refundLineItem.line_item_id?.toString() ===
          product.line_item_id?.toString()
        ) {
          product_subtotal += refundLineItem.subtotal;
          tax_subtotal += refundLineItem.total_tax;
          refund_currency =
            refundLineItem.subtotal_set?.presentment_money?.currency_code ??
            this.order.shopify?.presentment_currency;
        }
      }
    }
    return {
      refund_item: product_subtotal,
      refund_tax: tax_subtotal,
      total_refund: product_subtotal + tax_subtotal,
      refund_currency,
    };
  }

  getTotalsForProduct(product: Product): ProductTotals {
    const productTotals = this.productTotals.find(
      (p) =>
        (product._id &&
          product._id?.toString() === p.product._id?.toString()) ||
        (!product._id && product.line_item_id === p.product.line_item_id),
    );
    if (!productTotals) {
      throw new Error(
        `Product totals not found for product ${product.line_item_id}`,
      );
    }
    return productTotals;
  }

  /**
   * Calculate totals for a specific product, including discount price, price adjustments, and tax
   */
  private calculateTotalsForProduct(
    product: Product,
    loyalty: {
      returnValueDeductedDueToLoyaltyPoints: number;
      loyaltyPointsRefunded: number;
      loyaltyPointsDeducted: number;
    },
  ): ProductTotals {
    const order = this.getProductOrder(product);

    const shopifyLineItem = ReturnTotalsCalculator.findLineItemFromShopifyField(
      product.line_item_id,
      order,
    );

    const tax = this.productTax(product);
    const rawTax = this.productTax(product, { returnRawTax: true });
    let discountPrice;
    if (isDefined(product.percentOfBundle)) {
      discountPrice = shopifyLineItem
        ? parseFloat(
            ReturnTotalsCalculator.getDiscountPriceNew({
              lineItem: shopifyLineItem,
              order,
              originOrders: this.originOrders,
              logFn: this.logFn,
              percentOfBundle: product.percentOfBundle,
              distributionMethod:
                this.team.settings.discountDistributionMethod ?? undefined,
            }),
          )
        : 0;
    } else {
      discountPrice = product.isUnbundled
        ? parseFloat(product.price)
        : parseFloat(
            ReturnTotalsCalculator.getDiscountPriceOld(
              shopifyLineItem,
              order,
              this.originOrders,
              this.logFn,
              this.team.settings.discountDistributionMethod ?? undefined,
              product,
            ),
          );
    }

    discountPrice = Math.max(
      0,
      discountPrice - loyalty.returnValueDeductedDueToLoyaltyPoints,
    );

    const originPrice = ReturnTotalsCalculator.getOriginItemPrice(
      shopifyLineItem,
      order,
      this.originOrders,
      this.logFn,
    );
    const originalPrice =
      originPrice && order.isExchangeOrder
        ? parseFloat(originPrice)
        : product.isUnbundled
          ? parseFloat(product.price)
          : parseFloat(shopifyLineItem?.price);

    const { refund_item, refund_tax, total_refund, refund_currency } =
      this.getProductRefundTotals(product);

    return {
      product,
      discountPrice,
      originalPrice,
      priceAdjustment: parseFloat(
        this.getProductPriceAdjustment(product) || "0",
      ),
      merchantAdjustment: parseFloat(
        this.getProductMerchantAdjustment(product),
      ),
      dynamicBundleAdjustment: parseFloat(
        this.getDynamicBundlePriceAdjustment(product),
      ),
      finalPrice:
        discountPrice +
        tax +
        parseFloat(this.getProductPriceAdjustment(product) || "0") +
        parseFloat(this.getProductMerchantAdjustment(product)),
      tax,
      rawTax,
      duty: 0,
      totalDiscount:
        ReturnTotalsCalculator.getDiscountAllocations(
          shopifyLineItem,
          order,
          this.team.settings.discountDistributionMethod ?? undefined,
        ) / (shopifyLineItem?.quantity || 1),
      refund_item,
      refund_tax,
      total_refund,
      refund_currency,
      ...loyalty,
    };
  }

  /**
   * Calculate totals for all products of return
   */
  getTotalsForNonRejectedProducts() {
    return this.getTotalsForProducts(
      this.return_.products.filter(
        (product) => product.status !== ReturnedProductStatus.REJECTED,
      ),
    );
  }

  /**
   * Calculate totals for all products of return
   */
  getTotalsForAllProducts() {
    return this.getTotalsForProducts(this.return_.products);
  }

  cannotDeductDueToSalesChannels() {
    const salesChannelIdsToExclude = ["facebook"];
    const channelsToExclude = SalesChannels.filter((channel: Channel) =>
      salesChannelIdsToExclude.includes(channel.id),
    );
    return channelsToExclude.some((channel) =>
      channel.sourceNames.some((sourceName) =>
        (this.orders?.length ? this.orders : [this.order]).some((order) =>
          order.shopify.source_name?.startsWith(sourceName),
        ),
      ),
    );
  }

  /**
   * Calculate totals for specific products
   */
  getTotalsForProducts(
    products: Pick<Product, "_id" | "line_item_id">[],
    nonZeroValueExchange = false,
  ) {
    // This accommodates calculating totals for products both before and after the return has been
    // created. If the return has been created, we rely on the _id of the product. If not,
    // We rely on the line_item_id and quantities
    const productsToProcess: ProductTotals[] = [];
    for (const productTotals of this.productTotals) {
      // Ensure there aren't more productTotals with the same line item than are in lineItemIds
      const quantityRequested = products.filter(
        (product) =>
          product.line_item_id === productTotals.product.line_item_id,
      ).length;
      const quantityAdded = productsToProcess.filter(
        (product) =>
          product.product.line_item_id === productTotals.product.line_item_id,
      ).length;
      if (
        quantityAdded >= quantityRequested &&
        !products.some((product) => product._id !== null)
      ) {
        continue;
      }
      if (
        products.some(
          (product) =>
            (product._id &&
              product._id?.toString() ===
                productTotals.product._id?.toString()) ||
            (!product._id &&
              product.line_item_id === productTotals.product.line_item_id),
        )
      ) {
        productsToProcess.push(productTotals);
      }
    }

    const storeCreditTotals = this.getStoreCreditAmount(productsToProcess);
    const refundTotals = this.getRefundAmount(productsToProcess);

    const finalSaleCredit = this.getFinalSaleCredit(productsToProcess);
    const rawNewOrderValueTotals = this.getRawNewOrderValue(productsToProcess);
    const newOrderValueTotals = this.getTotalNewOrderValue(productsToProcess);
    const newOrderValue = newOrderValueTotals.amount + newOrderValueTotals.tax;

    const chargeTotals = {
      amount: newOrderValueTotals.amount,
      tax: newOrderValueTotals.tax,
    };

    // If there is new order value (or new order value is 0 but there is negative
    //  store credit due to fees), apply any existing store credit
    if (chargeTotals.amount || storeCreditTotals.amount < 0) {
      this.applyCreditToCharge(chargeTotals, storeCreditTotals);
    }

    // If there is new order value (or new order value is 0 but there is negative
    //  refund due to fees), apply any existing store credit
    if (chargeTotals.amount || refundTotals.amount < 0) {
      this.applyCreditToCharge(chargeTotals, refundTotals);
    }

    // Don't issue refund or store credit for a non-zero value exchange
    if (nonZeroValueExchange) {
      storeCreditTotals.amount = 0;
      storeCreditTotals.tax = 0;

      refundTotals.amount = 0;
      refundTotals.tax = 0;
    }

    // For merchants like Wild Oak who use pre-discount prices
    // E.g. original price = $100, discount price = $60
    // The difference ($40) can be applied for shop now but cannot rollover as additional store credit
    const nonAccruableCredit = this.getNonAccruableCredit(productsToProcess);
    let nonAccruableLeftover =
      nonAccruableCredit - (chargeTotals.amount + chargeTotals.tax);
    if (nonAccruableLeftover < 0) {
      nonAccruableLeftover = 0;
    }
    chargeTotals.amount -= nonAccruableCredit;
    if (chargeTotals.amount < 0) {
      chargeTotals.amount = 0;
    }
    if (chargeTotals.tax < 0) {
      chargeTotals.tax = 0;
    }

    const returnValueTotals = this.getTotalReturnValue(productsToProcess);
    const rawReturnValueTotals = this.getRawReturnValue(productsToProcess);
    const totalAdjustmentValue =
      this.getTotalAdjustmentValue(productsToProcess);
    const totalMerchantAdjustment =
      this.getTotalMerchantAdjustment(productsToProcess);
    const totalTaxes = this.getProductTotalsTaxes(productsToProcess);
    const totalRawTaxes = this.getProductTotalsTaxes(productsToProcess, {
      returnRawTax: true,
    });

    const totalReturnValue = returnValueTotals.amount + returnValueTotals.tax;
    let totalStoreCredit = storeCreditTotals.amount + storeCreditTotals.tax;
    let totalRefund = refundTotals.amount + refundTotals.tax;
    let totalFinalSaleCredit = finalSaleCredit.amount;
    let displayCharge = newOrderValue - (totalReturnValue + nonAccruableCredit);
    if (displayCharge < 0) {
      displayCharge = 0;
    }

    // When the customer-selected exchange shipping fee will be billed on
    // the exchange draft order (instead of via Stripe at submit), fold it
    // into the outstanding balance so display surfaces just read
    // `displayCharge` without re-adding the fee.
    const exchangeShippingFeeAmount = this.return_.exchangeShippingFee || 0;
    const chargesExchangeShippingViaStripe =
      computeChargesExchangeShippingViaStripe({
        forceExchangeStripeBilling:
          this.team.settings.exchanges?.forceExchangeStripeBilling,
        upsellBillingTrigger: this.return_.upsellBillingTrigger,
      });
    if (!chargesExchangeShippingViaStripe) {
      displayCharge += exchangeShippingFeeAmount;
    }

    if (this.return_.settlement?.accepted) {
      // Customer settled for a lower refund, so reduce total refund by the settlement
      totalRefund = totalReturnValue * this.return_.settlement.refund;
      totalFinalSaleCredit =
        finalSaleCredit.amount * this.return_.settlement.refund;
    }

    let shippingFee =
      this.return_.shippingFeeToDeduct || this.return_.totals?.fee || 0;
    let shippingFeeDeductedFromStoreCredit = 0;
    let shippingFeeDeductedFromRefund = 0;

    const exclude = this.cannotDeductDueToSalesChannels();
    // If we are not on the return portal (e.g. we're on the merchant dashboard), we should only deduct
    // the shipping fee if it was deducted in the return portal (i.e. labelDeductedFromCredit = true)
    if (
      (this.return_.labelDeductedFromCredit || this.isReturnPortal) &&
      !this.return_.merchant_waived_shipping_fee_deduction
    ) {
      // Deduct shipping fee from final sale merchant credit if it would be deducted from store credit or refund
      if (
        !exclude &&
        shippingFee > 0 &&
        ((totalStoreCredit > 0 && this.team.settings.deductLabelFromCredit) ||
          (totalRefund > 0 && this.team.settings.deductLabelFromRefund)) &&
        totalFinalSaleCredit >= shippingFee
      ) {
        totalFinalSaleCredit = totalFinalSaleCredit - shippingFee;
      }

      const canDeductFromRefund =
        (this.isReturnPortal && this.team.settings.deductLabelFromRefund) ||
        this.return_.labelDeductedFromCredit;
      if (
        !exclude &&
        totalRefund > 0 &&
        shippingFee > 0 &&
        canDeductFromRefund &&
        totalRefund >= shippingFee
      ) {
        // Deduct the shipping cost from refund value, but only when it can cover the whole cost
        totalRefund = totalRefund - shippingFee;
        shippingFeeDeductedFromRefund = shippingFee;
        shippingFee = 0;
      }

      const canDeductFromCredit =
        (this.isReturnPortal && this.team.settings.deductLabelFromCredit) ||
        this.return_.labelDeductedFromCredit;
      if (
        canDeductFromCredit &&
        totalStoreCredit > 0 &&
        shippingFee > 0 &&
        totalStoreCredit >= shippingFee
      ) {
        // Deduct the shipping cost from store credit, but only when it can cover the whole cost
        totalStoreCredit = totalStoreCredit - shippingFee;
        shippingFeeDeductedFromStoreCredit = shippingFee;
        shippingFee = 0;
      }

      if (this.return_.shippingFeeToDeduct && !exclude && shippingFee > 0) {
        // The shipping cost needs to be reducted from the refund and/or store credit value,
        // but the return is being partially processed, so deduct some now and the rest later
        const stepTwoDeductedFromRefund = shippingFee - totalRefund;
        shippingFeeDeductedFromRefund += stepTwoDeductedFromRefund;
        shippingFee -= totalRefund;
        totalRefund = shippingFee * -1;
        if (totalRefund < 0) {
          totalRefund = 0;
        }

        if (shippingFee > 0) {
          const stepTwoDeductedFromStoreCredit = shippingFee - totalStoreCredit;
          shippingFeeDeductedFromStoreCredit += stepTwoDeductedFromStoreCredit;
          shippingFee -= totalStoreCredit;
          totalStoreCredit = shippingFee * -1;
          if (totalStoreCredit < 0) {
            totalStoreCredit = 0;
          }
        }

        if (shippingFee < 0) {
          shippingFee = 0;
        }
      }
    }

    // Exchange-bucket fall-through: when the team/return is configured to
    // deduct shipping from exchange AND any shipping fee remains unabsorbed
    // by refund/credit AND the return has exchange items, fold the remainder
    // into `displayCharge` so it surfaces on the customer's exchange invoice.
    // Mirrors the equivalent waterfall in the RAC (`deductReturnShippingFee`).
    let shippingFeeDeductedFromExchange = 0;
    const exchangeFlagActive =
      this.return_.labelDeductedFromExchange ||
      (this.isReturnPortal && !!this.team.settings.deductLabelFromExchange);
    const hasExchangeItems =
      (this.return_.advancedExchangeItems?.length ?? 0) > 0 ||
      this.return_.products.some(
        (p) =>
          ("exchange_for" in p && !!p.exchange_for) || !!p.exchangeGroupItem,
      );
    if (
      shippingFee > 0 &&
      !exclude &&
      exchangeFlagActive &&
      hasExchangeItems &&
      !this.return_.merchant_waived_shipping_fee_deduction
    ) {
      shippingFeeDeductedFromExchange = shippingFee;
      displayCharge += shippingFee;
      shippingFee = 0;
    }

    let recoveryFee = 0;
    if (this.return_.provision === "instant" && this.return_.stripe) {
      recoveryFee = this.getRecoveryFee(
        productsToProcess,
        displayCharge,
        totalReturnValue,
        newOrderValue,
      );
    }

    let merchantAdjustmentsShouldBeAddedToSCRefund = false;
    // If there are positive merchant adjustments, we need to add them to the store credit/refund
    // amounts instead of deducting them from the charge, since the draft order was already created
    // and it is no longer possible to change the price
    if (this.return_.draftOrderId && this.return_.exchangeOrder?.length) {
      chargeTotals.amount = 0;

      if (
        newOrderValue >
        rawReturnValueTotals.amount + rawReturnValueTotals.tax
      ) {
        const storeCreditMerchantAdjustment = this.productTotals
          .filter(
            (product) =>
              this.getProductStrategy(product.product) === "store_credit",
          )
          .reduce((total, product) => {
            return total + product.merchantAdjustment;
          }, 0);

        const refundMerchantAdjustment = this.productTotals
          .filter(
            (product) => this.getProductStrategy(product.product) === "refund",
          )
          .reduce((total, product) => {
            return total + product.merchantAdjustment;
          }, 0);

        totalStoreCredit = storeCreditMerchantAdjustment;
        if (totalStoreCredit < 0) {
          totalStoreCredit = 0;
        }

        totalRefund = refundMerchantAdjustment;
        if (totalRefund < 0) {
          totalRefund = 0;
        }

        totalFinalSaleCredit = this.productTotals
          .filter((product) => product.product.isFinalSaleReturn)
          .reduce((total, product) => {
            return total + product.merchantAdjustment;
          }, 0);

        if (
          totalStoreCredit > 0 ||
          totalRefund > 0 ||
          totalFinalSaleCredit > 0
        ) {
          merchantAdjustmentsShouldBeAddedToSCRefund = true;
        }
      }
    }
    const newOrderDiscount = this.getReturnDiscountAllocations()
      ? this.getNewOrderDiscountedAmount()
      : 0;
    const newOrderTaxes = this.getReturnNewOrderTaxes();

    const totalDiscount = this.getTotalDiscount(productsToProcess);

    let totalOrderShipping = 0;
    const orders = this.orders || [this.order];
    for (const order of orders) {
      const orderShipping = this.getTotalOrderShipping(order);
      if (orderShipping > 0) {
        totalOrderShipping += orderShipping;
      }
    }

    // `displayCharge` already includes the draft-billed exchange shipping
    // fee (folded above); add the Stripe-billed portion here so
    // `totalPaymentOwed` is the true sum of everything the customer owes.
    let totalPaymentOwed = displayCharge;
    if (!this.return_.labelDeductedFromCredit) {
      totalPaymentOwed += shippingFee;
    }
    if (chargesExchangeShippingViaStripe) {
      totalPaymentOwed += exchangeShippingFeeAmount;
    }

    const allProductsAreStoreCredit = this.productTotals.every(
      (product) => product.product.strategy === "store_credit",
    );

    if (
      totalRefund <
      (this.team.settings.returns?.waiveRefundStoreCreditBelowAmount || 0)
    ) {
      totalRefund = 0;
    }
    if (
      totalStoreCredit <
      (this.team.settings.returns?.waiveRefundStoreCreditBelowAmount || 0)
    ) {
      totalStoreCredit = 0;
    }

    let totalStoreCreditWithShipping = totalStoreCredit;
    let totalRefundWithShipping = totalRefund;
    const originalShipping = this.return_.refundOriginalShipping
      ? this.return_.refundOriginalShippingAmount || 0
      : 0;
    // If all the products are store credit or exchange, we can add the original shipping to the store credit value
    if (allProductsAreStoreCredit) {
      totalStoreCreditWithShipping += originalShipping;
    } else {
      totalRefundWithShipping += originalShipping;
    }

    const totalLoyaltyPointsDeducted = this.productTotals.reduce(
      (total, product) => {
        return total + product.loyaltyPointsDeducted;
      },
      0,
    );
    const totalLoyaltyPointsRefunded = this.productTotals.reduce(
      (total, product) => {
        return total + product.loyaltyPointsRefunded;
      },
      0,
    );
    const totalReturnValueDeductedDueToLoyaltyPoints =
      this.productTotals.reduce((total, product) => {
        return total + product.returnValueDeductedDueToLoyaltyPoints;
      }, 0);

    const totals: ReturnTotals = {
      productTotals: this.productTotals,
      totalStoreCredit,
      totalStoreCreditWithShipping,
      totalRefundWithoutShipping: totalRefund,
      totalRefundWithShipping,
      totalReturnValue,
      totalRawReturnValue: rawReturnValueTotals.amount,
      totalAdjustmentValue,
      totalMerchantAdjustment,
      totalDiscount,
      newOrderValue,
      rawNewOrderValue: rawNewOrderValueTotals.amount,
      newOrderDiscount,
      totalNewOrderTaxes: rawNewOrderValueTotals.tax,
      advancedExchangeItemsTaxInNewOrder: newOrderTaxes,
      advancedExchangeItemsValue: this.getAdvancedExchangeItemsFee().amount,
      totalTaxes,
      totalDuty: 0,
      totalRawTaxes,
      charge: chargeTotals.amount,
      displayCharge,
      recoveryFee,
      shippingFee,
      shippingFeeDeducted: (this.return_.totals?.fee || 0) - shippingFee,
      shippingFeeDeductedFromStoreCredit,
      shippingFeeDeductedFromRefund,
      shippingFeeDeductedFromExchange,
      customerLabelUpchargeFee:
        this.return_.totals?.customerLabelUpchargeFee || 0,
      nonAccruableCredit,
      nonAccruableLeftover,
      finalSaleCredit: Math.max(totalFinalSaleCredit, 0),
      totalOrderShipping,
      totalPaymentOwed,
      merchantAdjustmentsShouldBeAddedToSCRefund,
      totalLoyaltyPointsDeducted,
      totalLoyaltyPointsRefunded,
      totalReturnValueDeductedDueToLoyaltyPoints,
      exchangeShippingFee: exchangeShippingFeeAmount,
      exchangeShippingName: this.return_.exchangeShippingName || "",
      chargesExchangeShippingViaStripe,
    };
    totals.productTotals = totals.productTotals.map((product) => {
      return {
        ...product,
        product: {
          _id: product.product._id || "",
          line_item_id: product.product.line_item_id,
          variant_id: product.product.variant_id,
          price: product.product.price,
          price_adjustment: product.product.price_adjustment,
          merchant_adjustment: product.product.merchant_adjustment || "",
          strategy: product.product.strategy,
          exchangeGroupItem: product.product.exchangeGroupItem || undefined,
          exchange_for:
            "exchange_for" in product.product
              ? product.product.exchange_for
              : undefined,
          new_item:
            "new_item" in product.product
              ? product.product.new_item
              : undefined,
        },
      };
    });
    return totals;
  }

  private applyCreditToCharge(chargeTotals: Totals, creditTotals: Totals) {
    const totalCharge = chargeTotals.amount + chargeTotals.tax;
    const totalCredit = creditTotals.amount + creditTotals.tax;

    if (totalCharge > totalCredit) {
      chargeTotals.amount -= creditTotals.amount;
      chargeTotals.tax -= creditTotals.tax;

      creditTotals.amount = 0;
      creditTotals.tax = 0;
    } else {
      creditTotals.amount -= chargeTotals.amount;
      creditTotals.tax -= chargeTotals.tax;

      chargeTotals.amount = 0;
      chargeTotals.tax = 0;
    }

    if (creditTotals.amount < 0 || creditTotals.tax < 0) {
      creditTotals.amount += creditTotals.tax;
      creditTotals.tax = 0;
    }

    if (chargeTotals.amount < 0 || chargeTotals.tax < 0) {
      chargeTotals.amount += chargeTotals.tax;
      chargeTotals.tax = 0;
    }
  }

  /**
   * Get the total price of all items being returned for a specific return type (credit, refund, or exchange)
   *
   * Note: the merchant adjustment (and price adjustment) is split between the product price and the product tax.
   *  Example: productPrice = $10, tax = $0.72, merchantAdjustment = -$5
   *  merchantAdjustmentPercentage = ($10 + $0.72 - $5) / ($10 + $0.72) = 0.534
   *  productPrice = $10 * 0.534 = $5.34
   *  tax = $0.72 * 0.534 = $0.38
   *  total = $5.34 + $0.38 = $5.72 (which is the same as $10 + $0.72 - $5)
   *
   * This is important when applying exchange discounts to draft orders, we apply it without taxes since
   *  taxes are calculated after discounts.
   */
  private getTotalValueForType(
    strategy: "refund" | "store_credit",
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    let amount = 0;
    let tax = 0;
    for (const productTotals of productsToProcess) {
      if (productTotals.product.isNewItem) {
        continue;
      }
      if (this.getProductStrategy(productTotals.product) === strategy) {
        const marketAdjustmentRate =
          strategy === "refund"
            ? 1
            : (this.getMarketRateAdjustmentForProduct(productTotals.product) ??
              1);

        if (includeAdjustments) {
          const adjustments =
            productTotals.priceAdjustment + productTotals.merchantAdjustment;
          const productPrice =
            (productTotals.discountPrice + productTotals.tax) *
            marketAdjustmentRate;
          if (productPrice === 0) {
            amount += adjustments;
          } else {
            const adjustmentPercentage =
              (productPrice + adjustments) / productPrice;
            amount +=
              productTotals.discountPrice *
              adjustmentPercentage *
              marketAdjustmentRate;
            tax +=
              productTotals.tax * adjustmentPercentage * marketAdjustmentRate;
          }
        } else {
          amount += productTotals.discountPrice * marketAdjustmentRate;
          tax += productTotals.tax * marketAdjustmentRate;
        }
      }
    }
    if (this.isInPresentmentCurrency) {
      amount = (this.getExchangeRate() ?? 1) * amount;
      tax = (this.getExchangeRate() ?? 1) * tax;
    }

    return { amount, tax };
  }

  getTotalAdjustmentValue(productsToProcess: ProductTotals[]) {
    return productsToProcess.reduce((total, product) => {
      return total + product.priceAdjustment;
    }, 0.0);
  }

  getTotalMerchantAdjustment(productsToProcess: ProductTotals[]) {
    let totalAdjustment = productsToProcess.reduce((total, product) => {
      return total + product.merchantAdjustment;
    }, 0.0);
    const newOrderPriceAdjustment = this.return_.newOrderPriceAdjustment;
    if (newOrderPriceAdjustment) {
      if (newOrderPriceAdjustment.type === "amount") {
        totalAdjustment += newOrderPriceAdjustment.value || 0;
      } else if (newOrderPriceAdjustment.type === "percentage") {
        const rawNewOrderValue = this.getRawNewOrderValue(productsToProcess);
        totalAdjustment +=
          (newOrderPriceAdjustment.value / 100) *
          (rawNewOrderValue.amount + rawNewOrderValue.tax);
      }
    }
    return totalAdjustment;
  }

  getTotalDiscount(productTotals: ProductTotals[]) {
    return productTotals.reduce((total, product) => {
      return total + product.totalDiscount;
    }, 0);
  }

  /**
   * Total price of all items being returned for store credit
   */
  getStoreCreditAmount(
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    return this.getTotalValueForType(
      "store_credit",
      productsToProcess,
      includeAdjustments,
    );
  }

  /**
   * Total price of all items being returned for a refund
   */
  getRefundAmount(
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    return this.getTotalValueForType(
      "refund",
      productsToProcess,
      includeAdjustments,
    );
  }

  /**
   * Total price of all items being returned for a variant exchange
   */
  getVariantExchangeValue(
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    const variantExchangeProducts = productsToProcess.filter((productTotals) =>
      this.isVariantExchange(productTotals.product),
    );
    const storeCreditType = this.getTotalValueForType(
      "store_credit",
      variantExchangeProducts,
      includeAdjustments,
    );
    const refundType = this.getTotalValueForType(
      "refund",
      variantExchangeProducts,
      includeAdjustments,
    );

    return {
      amount: storeCreditType.amount + refundType.amount,
      tax: storeCreditType.tax + refundType.tax,
    };
  }

  /**
   * Total price of all new exchange items as part of the advanced exchange
   */
  getAdvancedExchangeItemsFee() {
    let exchangeFee = 0;
    for (const advancedExchangeItem of this.getReturnAdvancedExchangeItems()) {
      const valueToUse =
        this.return_.provider === Provider.COMMERCE_CLOUD
          ? advancedExchangeItem.itemValue
          : advancedExchangeItem.price;

      let itemFee =
        parseFloat(valueToUse || "0") * (advancedExchangeItem.quantity || 0);

      // Apply market rate adjustment if available
      if (advancedExchangeItem.variantId) {
        const marketAdjustmentRate =
          this.getMarketRateAdjustmentForProduct(
            { variant_id: advancedExchangeItem.variantId } as Product,
            true,
          ) ?? 1;
        if (!this.isInPresentmentCurrency) {
          itemFee *= marketAdjustmentRate;
        }
      }

      exchangeFee += itemFee;
    }
    if (this.return_.provider !== Provider.COMMERCE_CLOUD) {
      exchangeFee -= this.getNewOrderDiscountedAmount();
    }

    return { amount: exchangeFee, tax: this.getReturnNewOrderTaxes() };
  }

  /**
   * Total price of all items being returned
   */
  getTotalReturnValue(productsToProcess: ProductTotals[]) {
    if (this.isMerchantCreated) {
      let amount = 0;
      let tax = 0;
      for (const productTotals of productsToProcess) {
        if (!productTotals.product.isNewItem) {
          const marketAdjustmentRate =
            this.getMarketRateAdjustmentForProduct(productTotals.product) ?? 1;

          const adjustments =
            productTotals.priceAdjustment + productTotals.merchantAdjustment;
          const productPrice =
            (productTotals.discountPrice + productTotals.tax) *
            marketAdjustmentRate;
          if (productPrice === 0) {
            amount += adjustments;
          } else {
            const adjustmentPercentage =
              (productPrice + adjustments) / productPrice;
            amount +=
              productTotals.discountPrice *
              adjustmentPercentage *
              marketAdjustmentRate;
            tax +=
              productTotals.tax * adjustmentPercentage * marketAdjustmentRate;
          }
        }
      }

      return { amount, tax };
    } else {
      const storeCredit = this.getStoreCreditAmount(productsToProcess);
      const refund = this.getRefundAmount(productsToProcess);
      return {
        tax: storeCredit.tax + refund.tax,
        amount: storeCredit.amount + refund.amount,
      };
    }
  }

  /**
   * Total price of all items being returned not including adjustments
   */
  getRawReturnValue(productsToProcess: ProductTotals[]) {
    const storeCredit = this.getStoreCreditAmount(productsToProcess, false);
    const refund = this.getRefundAmount(productsToProcess, false);
    return {
      tax: storeCredit.tax + refund.tax,
      amount: storeCredit.amount + refund.amount,
    };
  }

  /**
   * Total price of all items in the new order (including variant and advanced exchange items)
   */
  getTotalNewOrderValue(productsToProcess: ProductTotals[]) {
    if (this.shouldCancelExchangeOrder()) {
      return { tax: 0, amount: 0 };
    }
    const variantExchangeValue = this.getVariantExchangeValue(
      productsToProcess,
      false,
    );
    const advancedExchangeItemsFee = this.getAdvancedExchangeItemsFee();
    let totalTax = variantExchangeValue.tax + advancedExchangeItemsFee.tax;
    let totalAmount =
      variantExchangeValue.amount + advancedExchangeItemsFee.amount;
    const newOrderPriceAdjustment = this.return_.newOrderPriceAdjustment;
    if (newOrderPriceAdjustment) {
      if (newOrderPriceAdjustment.type === "amount") {
        if (totalAmount === 0) {
          totalAmount += newOrderPriceAdjustment.value;
        } else {
          const adjustmentPercentage =
            (totalAmount + totalTax + (newOrderPriceAdjustment.value || 0)) /
            (totalAmount + totalTax);
          totalTax *= adjustmentPercentage;
          totalAmount *= adjustmentPercentage;
        }
      } else if (newOrderPriceAdjustment.type === "percentage") {
        totalTax *= 1 - newOrderPriceAdjustment.value / 100;
        totalAmount *= 1 - newOrderPriceAdjustment.value / 100;
      }
    }
    return { tax: totalTax, amount: totalAmount };
  }

  /**
   * Total price of an advanced exchange item. Takes into account the price adjustment in "newOrderPriceAdjustment" if applicable
   */
  getAdvancedExchangeItemPrice(variantId: string) {
    const advancedExchangeItem = this.getReturnAdvancedExchangeItems().find(
      (item) => item.variantId === variantId,
    );
    if (
      !advancedExchangeItem ||
      !advancedExchangeItem.price ||
      !advancedExchangeItem.tax
    ) {
      return;
    }

    let priceAdjustment = 0;
    let taxAdjustment = 0;
    const totalAdjustment =
      (this.return_.newOrderPriceAdjustment?.value || 0) *
      (+advancedExchangeItem.price /
        this.getRawNewOrderValue(this.productTotals).amount);
    const itemPricePlusTax =
      +advancedExchangeItem.price + +advancedExchangeItem.tax;
    switch (this.return_.newOrderPriceAdjustment?.type) {
      case "percentage":
        priceAdjustment =
          this.return_.newOrderPriceAdjustment.value *
          0.01 *
          +advancedExchangeItem.price;
        taxAdjustment =
          this.return_.newOrderPriceAdjustment.value *
          0.01 *
          +advancedExchangeItem.tax;
        break;
      case "amount":
        priceAdjustment =
          totalAdjustment * (+advancedExchangeItem.price / itemPricePlusTax);
        taxAdjustment =
          totalAdjustment * (+advancedExchangeItem.tax / itemPricePlusTax);
        break;
    }

    const originalPrice = +advancedExchangeItem.price - priceAdjustment;
    const price = +(advancedExchangeItem.itemValue || originalPrice);
    const tax = +advancedExchangeItem.tax - taxAdjustment;

    return { price, originalPrice, tax };
  }

  getRawNewOrderValue(productsToProcess: ProductTotals[]) {
    const variantExchangeValue = this.getVariantExchangeValue(
      productsToProcess,
      false,
    );
    const advancedExchangeItemsFee = this.getAdvancedExchangeItemsFee();
    return {
      tax: variantExchangeValue.tax + advancedExchangeItemsFee.tax,
      amount: variantExchangeValue.amount + advancedExchangeItemsFee.amount,
    };
  }

  getRecoveryFee(
    productsToProcess: ProductTotals[],
    charge: number,
    returnValue: number,
    newOrderValue: number,
  ) {
    const totalMerchantAdjustments = productsToProcess.reduce(
      (total, product) => total + product.merchantAdjustment,
      0,
    );
    if (returnValue - totalMerchantAdjustments > newOrderValue) {
      return charge;
    }

    return totalMerchantAdjustments > 0 ? 0 : totalMerchantAdjustments * -1;
  }

  isVariantExchange(product: Product) {
    return (
      (product.exchangeGroupItem ||
        ("exchange_for" in product && product.exchange_for) ||
        ("new_item" in product && product.new_item)) &&
      !product.outOfStockDuringProcessing // Don't count it as variant exchange if out of stock
    );
  }

  getFinalSaleCredit(productsToProcess: ProductTotals[]) {
    const finalSaleProducts = productsToProcess.filter(
      (product) => product.product.isFinalSaleReturn,
    );
    const storeCreditTotals = this.getStoreCreditAmount(finalSaleProducts);
    const refundTotals = this.getRefundAmount(finalSaleProducts);
    return {
      amount: storeCreditTotals.amount + refundTotals.amount,
      tax: storeCreditTotals.tax + refundTotals.tax,
    };
  }

  shouldCancelExchangeOrder() {
    if (
      this.team.settings.exchanges.outOfStockAdvanced !==
      OutOfStockAdvanced.CANCEL
    ) {
      return false;
    }

    return (
      this.return_.products.some(
        (product) => product.outOfStockDuringProcessing,
      ) ||
      this.return_.advancedExchangeItems.some(
        (product) => product.outOfStockDuringProcessing,
      )
    );
  }

  private static findLineItemFromShopifyField(
    lineItemId: string | number,
    order: Order | undefined,
  ) {
    return order?.shopify?.line_items.find(
      (lineItem) => String(lineItem.id) === String(lineItemId),
    );
  }

  private getUsePreDiscountPrice() {
    return (
      (this.isReturnPortal &&
        this.team.settings.exchanges.usePreDiscountPrice) ||
      this.return_.usePreDiscountPrice
    );
  }

  private getNonAccruableCredit(productsToProcess: ProductTotals[]) {
    let amount = 0;
    if (this.getUsePreDiscountPrice()) {
      for (const product of productsToProcess) {
        if (
          this.isVariantExchange(product.product) &&
          !this.team.settings.differentPricedVariantExchanges
        ) {
          continue;
        }
        const nonAccruableAmount =
          product.originalPrice - product.discountPrice;

        amount += nonAccruableAmount;
      }
    }

    return amount;
  }

  private getProductStrategy(product: Product) {
    if (this.useOriginalReturn && product.originalVariantOptions) {
      return product.originalVariantOptions.strategy;
    }
    return product.strategy;
  }

  private getProductPriceAdjustment(product: Product) {
    if (this.useOriginalReturn && product.originalVariantOptions) {
      return product.originalVariantOptions.price_adjustment;
    }
    return product.price_adjustment;
  }

  private getProductMerchantAdjustment(product: Product) {
    // Merchant adjustments happen after the return is created, so if we are
    // calculating for the original return, we shouldn't use merchant adjustments
    // (different than price_adjustment which is calculated in the return portal)
    if (this.useOriginalReturn || !product.merchant_adjustment) {
      return "0";
    }
    return product.merchant_adjustment;
  }

  private getDynamicBundlePriceAdjustment(product: Product): string {
    return (
      product.adjustments?.find(
        (adjustment) =>
          adjustment.type === AdjustmentType.DYNAMIC_BUNDLE_ADJUSTMENT,
      )?.flat.shopMoney?.amount ?? "0"
    );
  }

  private getReturnAdvancedExchangeItems() {
    let advancedExchangeItems: AdvancedExchangeItem[] = [];
    if (this.useOriginalReturn && this.return_.originalReturnOptions) {
      advancedExchangeItems =
        this.return_.originalReturnOptions.advancedExchangeItems || [];
    }
    advancedExchangeItems = this.return_.advancedExchangeItems;

    return advancedExchangeItems.filter(
      (advancedExchangeItem) =>
        advancedExchangeItem.outOfStockDuringProcessing !== true,
    );
  }

  /** Only includes taxes on the advanced exchange items, not items being exchanged for the same item */
  private getReturnNewOrderTaxes(): number {
    if (this.shouldCancelExchangeOrder()) {
      return 0;
    }
    if (this.useOriginalReturn && this.return_.originalReturnOptions) {
      return parseFloat(
        this.return_.originalReturnOptions.new_order_taxes || "0",
      );
    }
    if (
      this.return_.advancedExchangeItems.some(
        (newItem) => newItem.outOfStockDuringProcessing === true,
      )
    ) {
      const reduceFn = (price: number, newItem: AdvancedExchangeItem) => {
        let itemPrice =
          parseFloat(newItem.price || "0") * (newItem.quantity || 1);

        // Apply market rate adjustment if available
        if (newItem.variantId) {
          const marketAdjustmentRate =
            this.getMarketRateAdjustmentForProduct({
              variant_id: newItem.variantId,
            } as Product) ?? 1;
          itemPrice *= marketAdjustmentRate;
        }

        return price + itemPrice;
      };

      // Contains all items, including out of stock
      const originalPrice = this.return_.advancedExchangeItems.reduce(
        reduceFn,
        0,
      );
      // Does not contain out of stock items
      const currentPrice = this.getReturnAdvancedExchangeItems().reduce(
        reduceFn,
        0,
      );

      const taxRate = originalPrice
        ? parseFloat(this.return_.new_order_taxes || "0") / originalPrice
        : 0;

      return taxRate * currentPrice;
    }

    return parseFloat(this.return_.new_order_taxes || "0");
  }

  private getNewOrderDiscountedAmount() {
    const discountAllocations = this.getReturnDiscountAllocations();
    if (!discountAllocations) {
      return 0;
    }

    let discountedAmount = 0;
    for (const discount of discountAllocations) {
      discountedAmount += parseFloat(discount.discountedAmount.amount);
    }
    if (this.useOriginalReturn) {
      return discountedAmount;
    }

    // If some advancedExchangeItems are out of stock, we need to adjust
    // the discount to only apply to the remaining items
    if (
      this.return_.advancedExchangeItems.some(
        (newItem) => newItem.outOfStockDuringProcessing === true,
      )
    ) {
      const reduceFn = (price: number, newItem: AdvancedExchangeItem) => {
        let itemPrice =
          parseFloat(newItem.price || "0") * (newItem.quantity || 1);

        // Apply market rate adjustment if available
        if (newItem.variantId) {
          const marketAdjustmentRate =
            this.getMarketRateAdjustmentForProduct(
              { variant_id: newItem.variantId } as Product,
              true,
            ) ?? 1;
          itemPrice *= marketAdjustmentRate;
        }

        return price + itemPrice;
      };

      // Contains all items, including out of stock
      const originalPrice = this.return_.advancedExchangeItems.reduce(
        reduceFn,
        0,
      );
      // Does not contain out of stock items
      const currentPrice = this.getReturnAdvancedExchangeItems().reduce(
        reduceFn,
        0,
      );

      const discountPercentage = originalPrice
        ? discountedAmount / originalPrice
        : 0;

      discountedAmount = discountPercentage * currentPrice;
    }

    return discountedAmount;
  }

  private getReturnDiscountAllocations() {
    if (this.useOriginalReturn && this.return_.originalReturnOptions) {
      return this.return_.originalReturnOptions.discount_allocations;
    }
    return this.return_.discount_allocations;
  }

  private getTotalOrderShipping(order: Order) {
    const shippingPriceSet = order.shopify?.total_shipping_price_set;
    const amount = this.isInPresentmentCurrency
      ? (shippingPriceSet?.presentment_money?.amount ??
        shippingPriceSet?.shop_money?.amount)
      : shippingPriceSet?.shop_money?.amount;
    return Number(amount) || 0;
  }

  private getExchangeRate(): number | null {
    const shopCurrency = this.order.shopify?.currency;
    const presentmentCurrency = this.order.shopify?.presentment_currency;

    if (
      !shopCurrency ||
      !presentmentCurrency ||
      shopCurrency === presentmentCurrency
    ) {
      return null;
    }

    const shopAmount = Number(
      this.order.shopify?.total_price_set?.shop_money?.amount,
    );
    const presentmentAmount = Number(
      this.order.shopify?.total_price_set?.presentment_money?.amount,
    );

    if (!shopAmount || !presentmentAmount || presentmentAmount === 0) {
      return null;
    }

    return presentmentAmount / shopAmount;
  }

  private shouldApplyMarketRateAdjustment(): boolean {
    return !!(
      this.marketRate &&
      this.order.shopify?.currency &&
      this.order.shopify?.presentment_currency &&
      this.order.shopify.currency !== this.order.shopify.presentment_currency &&
      !this.isInPresentmentCurrency
    );
  }

  private getMarketRateAdjustmentForProduct(
    product: Product,
    newItem?: boolean,
  ): number | null {
    if (!this.shouldApplyMarketRateAdjustment() || !product.variant_id) {
      return null;
    }

    if (!this.marketRate) {
      return null;
    }

    const exchangeRate = newItem
      ? this.newItemToExchangeRate
      : this.getExchangeRate();
    if (!exchangeRate) {
      return null;
    }

    // Calculate the market rate adjustment factor:
    // exchangeRate => shop -> presentment (using shopify's live exchange rate)
    // marketRate.presentmentToShopRate => presentment -> shop (with the MARKET RATE)
    return exchangeRate * this.marketRate;
  }
}
